import React, { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Calendar, User, Clock, AlertCircle, Shield, Mail, UserCheck, Edit } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import AuthContext from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import activityService from '../../../../services/activityService';
import UpdateRiskModal from '../risks/EditDuplicatedRisk';


export default function ActivityDetails() {
  const { activityId } = useParams();
  const { authTokens, hasRole } = useContext(AuthContext);
  const { showError } = useToast();
  const navigate = useNavigate();

  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);

  useEffect(() => {
    fetchActivityDetails();
  }, [activityId]);

  const fetchActivityDetails = async () => {
    setLoading(true);
    
    try {
      const result = await activityService.getActivityDetails(activityId, authTokens.access);
      
      if (result.success) {
        setActivity(result.data);
        console.log(result.data);
      } else {
        showError(result.error || 'Erreur lors du chargement des détails');
      }
    } catch (error) {
      console.error('Error fetching activity details:', error);
      showError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const openUpdateModal = (risk) => {
    console.log('Opening modal for risk:', risk);
    setSelectedRisk(risk);
    setShowUpdateModal(true);
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setSelectedRisk(null);
  };

  const handleUpdateSuccess = () => {
    closeUpdateModal();
    fetchActivityDetails(); // Refresh the activity details
  };

  const canUpdateRisk = () => {
    return  hasRole("RiskManager");
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
    if (status?.toLowerCase() === 'active') {
      return `${baseClasses} bg-green-100 text-green-800`;
    } else if (status?.toLowerCase() === 'inactive') {
      return `${baseClasses} bg-red-100 text-red-800`;
    }
    return `${baseClasses} bg-gray-100 text-gray-800`;
  };

  const getRiskProbabilityColor = (probability) => {
    const prob = probability?.toLowerCase();
    if (prob === 'high' || prob === 'élevée' || prob === 'critique') return 'text-red-600 bg-red-50';
    if (prob === 'medium' || prob === 'moyenne' || prob === 'majeur') return 'text-orange-600 bg-orange-50';
    if (prob === 'low' || prob === 'faible' || prob === 'normal') return 'text-yellow-600 bg-yellow-50';
    if (prob === 'mineur') return 'text-blue-600 bg-blue-50';
    return 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement des détails...</div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500">Activité non trouvée</p>
          <button
            onClick={() => navigate('/dashboard/activities')}
            className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            Retour aux activités
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/dashboard/activities/all')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{activity.activity_name}</h1>
            <p className="text-sm text-gray-500">Détails de l'activité</p>
          </div>
        </div>
        <span className={getStatusBadge(activity.status)}>{activity.status}</span>
      </div>

      {/* Main Information Card */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Informations générales</h2>
        
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-500">Description</label>
            <p className="mt-1 text-gray-900">{activity.description || 'Aucune description'}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-gray-200">
            <div className="flex items-start space-x-2">
              <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Date de création</label>
                <p className="text-sm text-gray-900">{formatDate(activity.created_at)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <label className="text-sm font-medium text-gray-500">Dernière mise à jour</label>
                <p className="text-sm text-gray-900">{formatDate(activity.updated_at)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Creator & Updater Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {activity.creator_details && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div className="flex items-center space-x-2 mb-3">
              <User className="h-4 w-4 text-gray-400" />
              <h3 className="text-base font-semibold text-gray-900">Créé par</h3>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-900 font-medium">
                {activity.creator_details.first_name} {activity.creator_details.last_name}
              </p>
              <p className="text-xs text-gray-500">{activity.creator_details.email}</p>
            </div>
          </div>
        )}

        {activity.last_updater_details && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div className="flex items-center space-x-2 mb-3">
              <User className="h-4 w-4 text-gray-400" />
              <h3 className="text-base font-semibold text-gray-900">Dernière modification par</h3>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-900 font-medium">
                {activity.last_updater_details.first_name} {activity.last_updater_details.last_name}
              </p>
              <p className="text-xs text-gray-500">{activity.last_updater_details.email}</p>
            </div>
          </div>
        )}
      </div>

      {/* Associated Risks */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Risques associés</h2>
            <span className="px-2 py-1 bg-teal-100 text-teal-800 text-xs font-medium rounded-full">
              {activity.associated_risks?.length || 0} risque(s)
            </span>
          </div>
        </div>

        {activity.associated_risks && activity.associated_risks.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {activity.associated_risks.map((risk) => (
              <div key={risk.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      {risk.risk_name}
                    </h3>
                    <p className="text-sm text-gray-600">{risk.description}</p>
                  </div>
                  <div className="ml-3 flex flex-col items-end space-y-1">
                    {canUpdateRisk() && (
                      <button 
                        type="button" 
                        onClick={() => openUpdateModal(risk)}
                        className="flex items-center px-3 py-1.5 bg-gray-900 text-white hover:bg-gray-800 rounded-md text-sm font-medium transition-colors"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Modifier
                      </button>
                    )}
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskProbabilityColor(risk.probability)}`}>
                      {risk.probability}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      risk.status?.toLowerCase() === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {risk.status}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Conséquence</label>
                    <p className="text-sm text-gray-900 mt-1">{risk.consequence}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Date du risque</label>
                    <p className="text-sm text-gray-900 mt-1">{formatDate(risk.risk_date)}</p>
                  </div>
                </div>

                {/* Risk Actions */}
                {risk.actions && risk.actions.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <h6 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Shield className="h-4 w-4 mr-1 text-blue-500" />
                      Actions ({risk.actions.length})
                    </h6>
                    
                    <div className="space-y-2">
                      {risk.actions.map((action) => (
                        <div key={action.id} className="bg-white rounded border p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm text-gray-900">{action.action}</p>
                              {action.dueDate && (
                                <div className={`flex items-center mt-1 text-xs ${
                                  isOverdue(action.dueDate) ? 'text-red-600' : 'text-gray-500'
                                }`}>
                                  <Clock className="h-3 w-3 mr-1" />
                                  Échéance: {formatDate(action.dueDate)}
                                  {isOverdue(action.dueDate) && (
                                    <span className="ml-2 px-1 py-0.5 bg-red-100 text-red-800 rounded text-xs">
                                      En retard
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {action.assigned_user && (
                              <div className="ml-3 text-right">
                                <div className="flex items-center text-sm text-gray-900">
                                  <UserCheck className="h-4 w-4 mr-1 text-green-600" />
                                  {action.assigned_user.first_name} {action.assigned_user.last_name}
                                </div>
                                <div className="flex items-center text-xs text-gray-500 mt-1">
                                  <Mail className="h-3 w-3 mr-1" />
                                  {action.assigned_user.email}
                                </div>
                                <div className="mt-1">
                                  <span className="inline-block px-1 py-0.5 bg-blue-100 text-blue-800 text-xs rounded mr-1">
                                    {action.assigned_user.role_function}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <AlertCircle className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">Aucun risque associé à cette activité</p>
          </div>
        )}
      </div>

      {/* Update Risk Modal */}
      {showUpdateModal && selectedRisk && (
        <UpdateRiskModal
          risk={selectedRisk}
          onClose={closeUpdateModal}
          onUpdateSuccess={handleUpdateSuccess}
        />
      )}
    </div>
  );
}




















